import type { ReactNode } from 'react';
import { cx } from './cx';

interface StateProps {
  /** A sentence, not a label. "No announcements yet", not "Empty". */
  title: ReactNode;
  /** What the reader can do about it, or why it is empty. */
  description?: ReactNode;
  /** The way out — usually a C1Button. */
  action?: ReactNode;
  /** Decorative glyph above the title. */
  icon?: ReactNode;
  /** Tightens the padding, for a state inside a panel rather than a whole page. */
  compact?: boolean;
  className?: string;
}

function StateBlock({
  title,
  description,
  action,
  icon,
  compact,
  className,
  tone,
  role,
}: StateProps & { tone: 'empty' | 'error'; role?: 'alert' }) {
  return (
    <div
      className={cx('c1-state', `c1-state--${tone}`, compact && 'c1-state--compact', className)}
      role={role}
    >
      {icon ? (
        <span className="c1-stateIcon" aria-hidden="true">
          {icon}
        </span>
      ) : null}
      <p className="c1-stateTitle">{title}</p>
      {description ? <p className="c1-stateDescription">{description}</p> : null}
      {action ? <div className="c1-stateAction">{action}</div> : null}
    </div>
  );
}

/**
 * Nothing here yet.
 *
 * NOT AN ERROR, AND MUST NOT LOOK LIKE ONE. An empty list is usually the correct
 * state of a new institution, and dressing it in warning colours tells an
 * administrator something is broken on their first day. Quiet, and where possible
 * carrying the action that would fill it.
 */
export function C1Empty(props: StateProps) {
  return <StateBlock {...props} tone="empty" />;
}

/**
 * Something failed.
 *
 * `role="alert"` because this replaces content the reader asked for and is
 * appearing after the page settled — they need to be told, not left waiting for a
 * list that is never coming.
 *
 * SAY WHAT FAILED AND WHAT TO DO. "Something went wrong" with no retry leaves the
 * reader reloading the whole console to find out whether it was them.
 */
export function C1Error(props: StateProps) {
  return <StateBlock {...props} tone="error" role="alert" />;
}
